import { useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, Menu, MenuItem, styled, Typography } from '@mui/material';
import type { ValuationStatusSelectProps } from './ValuationStatusSelect.props';

const StatusTrigger = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: '240px',
  padding: '8px 12px',
  borderRadius: 16,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  textTransform: 'none',
  justifyContent: 'space-between',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    opacity: 1,
  },
}));

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: prop => prop !== 'isCompleted',
})<{ isCompleted: boolean }>(({ theme, isCompleted }) => ({
  minHeight: 58,
  margin: theme.spacing(1),
  padding: theme.spacing(1.5),
  gap: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent !important',
  },
  '& .MuiTypography-root': {
    color: isCompleted ? theme.palette.primary.main : theme.palette.common.black,
  },
}));

const ValuationStatusSelect = ({
  title = 'Status:',
  statusOptions,
  currentStatusId,
  onStatusChange,
  isLoading = false,
  disabled = false,
  id = 'valuation-status-select',
}: ValuationStatusSelectProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const currentStatusIndex = statusOptions.findIndex(status => status.id === currentStatusId);
  const selectedStatus = statusOptions.find(status => status.id === currentStatusId);
  const isTriggerDisabled = isLoading || disabled;
  const menuId = `${id}-menu`;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (statusId: string) => {
    handleClose();
    if (statusId !== currentStatusId) {
      onStatusChange?.(statusId);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
        {title}
      </Typography>
      <StatusTrigger
        id={id}
        aria-haspopup="menu"
        aria-expanded={isOpen ? 'true' : undefined}
        aria-controls={isOpen ? menuId : undefined}
        disabled={isTriggerDisabled}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        <CheckRoundedIcon sx={{ fontSize: 20, border: '1px solid #fff', borderRadius: '50%' }} />
        {selectedStatus?.label || 'Select status'}
      </StatusTrigger>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: {
            'aria-labelledby': id,
            sx: { p: 0.5 },
          },
          paper: {
            sx: { minWidth: 320, maxWidth: 440, borderRadius: 2 },
          },
        }}>
        {statusOptions.map((status, index) => {
          const isCompleted = currentStatusIndex >= 0 && index <= currentStatusIndex;

          return (
            <StyledMenuItem
              key={status.id}
              selected={status.id === currentStatusId}
              isCompleted={isCompleted}
              onClick={() => handleSelect(status.id)}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 2,
                  borderColor: isCompleted ? 'primary.main' : 'text.disabled',
                  borderRadius: '50%',
                  color: 'primary.main',
                }}>
                {isCompleted && <CheckRoundedIcon sx={{ fontSize: 20 }} />}
              </Box>
              <Typography sx={{ fontSize: '1rem' }}>{status.label}</Typography>
            </StyledMenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default ValuationStatusSelect;
